import { Link } from 'gatsby'
import React, { ChangeEvent, FC, useMemo, useRef, useState } from 'react'
import BertholdTitle from 'src/components/atoms/bertholdTitle'
import { ExclusiveToggleGroup } from 'src/components/molecules/exclusiveToggleGroup'
import { FilterRail } from 'src/components/molecules/filterRail'
import { RichTextDisclosure } from 'src/components/molecules/richTextDisclosure'
import Eyebrow from 'src/images/svgs/Eyebrow.svg'
import ChevronLeftIcon from 'src/images/svgs/icons/ChevronLeft.svg'
import MagnifyingGlassIcon from 'src/images/svgs/icons/MagnifyingGlass.svg'
import {
  PrismicFaqCategory,
  PrismicFaqCategoryDataQuestionAndAnswer,
  PrismicLinkType,
} from 'src/typings/generated/graphql'
import { generateRandomId } from 'src/utils/domHelper'
import { useFuzzySearch } from 'src/utils/useFuzzySearch'
import { useMediaQuery } from 'src/utils/useMediaQuery'
import { useTranslation } from 'src/utils/useTranslation'
import * as Styles from './searchableFaq.module.scss'

/**
 * Props for the `SearchableFaq` component.
 */
export type SearchableFaqProps = {
  /**
   * The HTML `id` attribute for the FAQ section. If not provided, a random ID will be generated.
   */
  anchor?: string
  /**
   * The headline text for the FAQ section. If not provided, the slice's content will not be rendered.
   */
  headline?: string
  /**
   * The title for the category list for wide viewports. If not provided, it will not be rendered.
   */
  categoryListTitle?: string
  /**
   * The list of FAQ categories to display. If not provided or empty, the slice's content will not be rendered.
   */
  categories?: PrismicFaqCategory[]
  /**
   * The text for the back button. If not provided, the back button will not be rendered.
   */
  backButtonText?: string
  /**
   * The link for the back button. If not provided, the back button will not be rendered.
   */
  backButtonLink?: PrismicLinkType
}

/**
 * FAQ section component that allows users to refine the FAQs displayed by searching for keywords and filtering by
 * category.
 */
export const SearchableFaq: FC<SearchableFaqProps> = ({
  anchor = generateRandomId(),
  headline,
  categoryListTitle,
  categories,
  backButtonText,
  backButtonLink,
}) => {
  const { t } = useTranslation()
  const { fuzzySearch } = useFuzzySearch()
  const isWideViewport = useMediaQuery('(min-width: 62em)')

  /**
   * Ref that stores the group name for all the `RichTextDisclosure` components used to display individual FAQs.
   *
   * This ensures that only one disclosure can be expanded at a time within this component.
   */
  const disclosureGroupNameRef = useRef(generateRandomId())

  const categoryListAndFaqSectionRef = useRef<HTMLDivElement>(null)

  const [rawSearchTerm, setRawSearchTerm] = useState('')
  const [activeCategoryId, setActiveCategoryId] = useState<string | undefined>(
    undefined
  )

  /**
   * Filters the list of FAQ categories based on the current search term and returns only those categories that have at least one FAQ that matches the search term.
   *
   * If no categories are provided, an empty array is returned.
   */
  const filteredCategories = useMemo(() => {
    if (!categories) {
      return []
    }

    const searchTerm = rawSearchTerm.trim()

    return (
      categories
        // Include all categories unless a specific category is selected, in which case include only that one
        .filter(
          category => !activeCategoryId || category.id === activeCategoryId
        )
        // Replace each category with a copy that contains only the FAQs that match the search term
        .map(category => {
          if (!category) {
            return null
          }

          const filteredFaqs = category.data?.question_and_answer?.filter(
            faq => {
              if (!faq || !faq.question?.text || !faq.answer?.richText.length) {
                return false
              }

              const questionMatches =
                fuzzySearch(
                  [{ question: faq.question.text }],
                  ['question'],
                  searchTerm
                ).length > 0

              // Consolidate the answer's rich text into a single string for searching
              const answerText = (faq.answer.richText as any[])
                .map(block => block?.text ?? '')
                .join(' ')

              const answerMatches =
                fuzzySearch([{ answer: answerText }], ['answer'], searchTerm)
                  .length > 0

              return questionMatches || answerMatches
            }
          )

          if (!filteredFaqs || filteredFaqs.length === 0) {
            return null
          }

          return {
            ...category,
            data: {
              ...category.data,
              question_and_answer: filteredFaqs,
            },
          }
        })
        // Remove any categories that ended up with no matching FAQs after filtering by search term
        .filter(category => category !== null) as PrismicFaqCategory[]
    )
  }, [categories, rawSearchTerm, fuzzySearch, activeCategoryId])

  /**
   * Handles changes to the search input field and updates the `rawSearchTerm` state accordingly.
   */
  const handleRawSearchTermChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value
    setRawSearchTerm(newSearchTerm)
  }

  /**
   * Scrolls the page to the start of the category list and FAQ section, only if the current scroll position is below
   * that location. This ensures that the user can see the filtered results after selecting or deselecting a category
   * filter, without unnecessarily scrolling the page if the user is already at or above that location.
   */
  const scrollToCategoryListAndFaqSectionStart = () => {
    const categoryListAndFaqSection = categoryListAndFaqSectionRef.current
    if (categoryListAndFaqSection) {
      const sectionTop = categoryListAndFaqSection.getBoundingClientRect().top
      const scrollMarginBlockStart = parseFloat(
        getComputedStyle(categoryListAndFaqSection).scrollMarginBlockStart
      )

      // Only scroll if the top of the section is below the scroll margin block start, meaning the user is currently scrolled below that location
      if (sectionTop < scrollMarginBlockStart) {
        categoryListAndFaqSection.scrollIntoView({
          behavior: 'smooth',
        })
      }
    }
  }

  const handleFilterBoxCategoryChange = (newCategoryId: string | undefined) => {
    scrollToCategoryListAndFaqSectionStart()
    setActiveCategoryId(newCategoryId)
  }

  /**
   * The message to display when no FAQs are found for the current search query.
   *
   * The message varies depending on whether a category filter is applied or not.
   */
  const emptyResultMessage = useMemo(
    () =>
      activeCategoryId
        ? t('components.SearchableFaq.emptySearchResultsForCategoryMessage')
        : t('components.SearchableFaq.emptySearchResultsMessage'),
    [activeCategoryId, t]
  )

  /**
   * Section containing the back button, if both its text and link are provided.
   */
  const backButtonSection = useMemo(() => {
    if (!backButtonText || !backButtonLink) {
      return null
    }

    return (
      <div className={Styles.backButtonSection}>
        <Link to={backButtonLink?.url ?? '#'} className={Styles.backButton}>
          <ChevronLeftIcon className={Styles.backButtonIcon} />
          {backButtonText}
        </Link>
      </div>
    )
  }, [backButtonText, backButtonLink])

  /**
   * Section containing the headline and search input.
   */
  const headlineAndSearchSection = useMemo(() => {
    return (
      <div className={Styles.headlineAndSearchSection}>
        <Eyebrow className={Styles.eyebrow} />
        <h2 className={Styles.headline}>{headline}</h2>
        <div className={Styles.searchInputWrapper}>
          <MagnifyingGlassIcon
            className={Styles.searchInputIcon}
            aria-hidden="true"
          />
          <input
            type="text"
            className={Styles.searchInput}
            placeholder={t('components.SearchableFaq.searchInputPlaceholder')}
            value={rawSearchTerm}
            aria-label={t('components.SearchableFaq.searchInputAriaLabel')}
            onChange={handleRawSearchTermChange}
          />
        </div>
      </div>
    )
  }, [headline, rawSearchTerm, t])

  /**
   * Section containing the category list and FAQ items.
   */
  const categoryListAndFaqSection = useMemo(() => {
    if (!categories) {
      return null
    }

    const mappedCategories = categories.map(category => ({
      label: category.data.title?.text ?? '',
      value: category.id,
    }))

    const filterElement = isWideViewport ? (
      <ExclusiveToggleGroup
        className={Styles.filterBox}
        items={mappedCategories}
        value={activeCategoryId}
        onChange={handleFilterBoxCategoryChange}
        title={categoryListTitle}
      />
    ) : (
      <FilterRail
        className={Styles.filterRail}
        items={mappedCategories}
        value={activeCategoryId}
        onChange={setActiveCategoryId}
        outerPadding="20px"
        emptyFilterLabel={t(
          'components.SearchableFaq.displayAllCategoriesButtonLabel'
        )}
      />
    )

    const renderFaqItem = (
      faq: PrismicFaqCategoryDataQuestionAndAnswer,
      key: string
    ): React.JSX.Element | null => {
      if (!faq.question?.text || !faq.answer?.richText) {
        return null
      }

      return (
        <li key={key} className={Styles.faqListItem}>
          <RichTextDisclosure
            groupName={disclosureGroupNameRef.current}
            summaryText={faq.question?.text}
            disclosureRichText={faq.answer}
          />
        </li>
      )
    }

    const renderFaqCategory = (
      category: PrismicFaqCategory
    ): React.JSX.Element => (
      <div key={category.id} className={Styles.faqListCategory}>
        <h3 className={Styles.faqListCategoryTitle}>
          {category?.data?.title
            ? BertholdTitle({ text: category.data.title.text ?? '' })
            : null}
        </h3>
        <ul className={Styles.faqList}>
          {category.data?.question_and_answer?.map((faq, index) =>
            faq
              ? renderFaqItem(
                  faq,
                  `faq_category_${category.id}_question_${index}`
                )
              : null
          )}
        </ul>
      </div>
    )

    return (
      <div
        ref={categoryListAndFaqSectionRef}
        className={Styles.categoryListAndFaqSection}
      >
        {filterElement}

        <div className={Styles.faqListContainer}>
          {filteredCategories.length > 0 ? (
            filteredCategories.map(renderFaqCategory)
          ) : (
            <p className={Styles.emptyResultMessage}>{emptyResultMessage}</p>
          )}
        </div>
      </div>
    )
  }, [
    categories,
    rawSearchTerm,
    activeCategoryId,
    setActiveCategoryId,
    filteredCategories,
  ])

  const shouldRenderContent = useMemo(
    () =>
      headline !== undefined &&
      categories !== undefined &&
      categories.length > 0,
    [headline, categories]
  )

  return (
    <section id={anchor} className={Styles.searchableFaq} aria-label={headline}>
      {shouldRenderContent && (
        <div className={Styles.content} data-testid="searchable-faq-content">
          {backButtonSection}

          {headlineAndSearchSection}

          {categoryListAndFaqSection}
        </div>
      )}
    </section>
  )
}
